import { createId } from '@paralleldrive/cuid2';
import { cloneDeep, forEach, isEqual } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { CommonEntityMetadata, MetadataFieldChangeMap } from 'types/Common.types';

/**
 * Initializes a new CommonEntityMetadata object for the given entry.
 * @param entry The entry to initialize the metadata for.
 * @param overrides Optional overrides for the metadata fields.
 * @returns The entry with the initialized metadata.
 * @example
 * ```typescript
 * const entry: ScenarioManagementEntry = {
 *  scenario_id: '123',
 *  page_no: '4',
 * }
 *
 * // Specify a type which extends from CommonEntityMetadata<>
 * const entryRow = initializeMetadata<ScenarioManagementRowModel>(entry);
 * // OR:           initializeMetadata(entry) as ScenarioManagementRowModel;
 *
 * console.log(entryRow);
 * // [LOG OUTPUT]
 * // {
 * //   scenario_id: '123',
 * //   page_no: '4',
 * //   metadata: {
 * //     internalId: 'abso2294381xs',
 * //     isNewRow: false,
 * //     isModified: false,
 * //     isDeleted: false,
 * //     fieldChanges: {},
 * //   }
 * // }
 * ```
 */
export const initializeMetadata = <TResult extends CommonEntityMetadata<TResult>>(
  entry: Omit<TResult, 'metadata'> & { metadata?: unknown },
  overrides?: Partial<CommonEntityMetadata<TResult>['metadata']>,
) => {
  return {
    ...entry,
    metadata: {
      internalId: createId(),
      isNewRow: false,
      isModified: false,
      isDeleted: false,
      fieldChanges: {} as MetadataFieldChangeMap<TResult>,
      ...overrides,
    },
  } as TResult;
};

/**
 * Resets an entity to its original state by restoring all field changes and clearing modification flags.
 *
 * This function reverts all tracked field changes back to their original values, effectively
 * undoing any modifications made to the entity. It creates a deep clone to ensure immutability
 * and resets all modification-related metadata flags.
 *
 * Key behaviors:
 * - Restores all fields to their original values from metadata.fieldChanges
 * - Clears the fieldChanges tracking object
 * - Sets isModified to false
 * - Sets isDeleted to false
 * - Preserves other metadata properties (internalId, isNewRow)
 *
 * @param entry - The entity to reset, must extend CommonEntityMetadata
 * @returns A new entry object with all changes reverted to original state
 *
 * @example
 * ```typescript
 * // originalEntry = {
 * //   title: 'Old Title',
 * //   metadata: { internalId: 'abc123', isModified: false, fieldChanges: {} }
 * // }
 *
 * const updatedEntry = changeEntryField(originalEntry, 'title', 'New Title');
 * // updatedEntry.title === 'New Title'
 * // updatedEntry.metadata.isModified === true
 * // updatedEntry.metadata.fieldChanges.title === { originalValue: 'Old Title', newValue: 'New Title' }
 *
 * // Reset back to original state
 * const resetEntry = resetEntry(updatedEntry);
 * // resetEntry.title === 'Old Title'
 * // resetEntry.metadata.isModified === false
 * // resetEntry.metadata.fieldChanges === {}
 * ```
 */
export const resetEntry = <TEntry extends CommonEntityMetadata<TEntry>>(entry: TEntry) => {
  const restoredEntry = cloneDeep(entry);
  if (restoredEntry.metadata.fieldChanges) {
    forEach(restoredEntry.metadata.fieldChanges, (change, field) => {
      restoredEntry[field as keyof TEntry] = change!.originalValue;
    });
  }
  restoredEntry.metadata.fieldChanges = {} as MetadataFieldChangeMap<TEntry>;
  restoredEntry.metadata.isModified = false;
  restoredEntry.metadata.isDeleted = false;
  return restoredEntry;
};

/**
 * Marks an entry as deleted by setting the isDeleted and isModified flags.
 * @param entry
 */
export const markEntryAsDeleted = <TEntry extends CommonEntityMetadata<TEntry>>(entry: TEntry) => {
  return initializeMetadata(resetEntry(entry), {
    internalId: entry.metadata.internalId,
    isDeleted: true,
    isModified: true,
  }) as TEntry;
};

/**
 * Applies field transformations to multiple entries using provided transformer functions.
 *
 * This utility function processes an array of entries and applies custom transformation logic
 * to specified fields. Each transformer receives the original entry and field name, allowing
 * for context-aware transformations that can reference other fields or metadata.
 *
 * **Important:** Use this function sparingly, typically just before API calls or final data
 * preparation. Unlike {@link changeEntryField} and {@link changeMultipleEntryFields}, field
 * transformations applied here bypass the metadata change tracking system. This means modified
 * fields won't be recorded in `metadata.fieldChanges` and won't affect `metadata.isModified`.
 * For tracked modifications during user interactions, use the dedicated change functions instead.
 *
 * Key behaviors:
 * - Creates shallow copies of entries to maintain immutability
 * - Applies transformers only to fields specified in the transformers object
 * - Preserves all other fields unchanged
 * - Processes all entries in the array independently
 * - Does NOT update metadata.fieldChanges or metadata.isModified
 *
 * @param entries - Array of entries to transform, must extend CommonEntityMetadata
 * @param transformers - Object mapping field names to transformer functions.
 *   Each transformer receives the original entry and field name, and returns the new field value.
 * @returns A new array of entries with specified fields transformed
 * @see {@link DecklinesContextProvider.saveDecklineChanges} for intended use-case.
 *
 * @example
 * ```typescript
 * const entries = [
 * { title: 'Page 1', page_no: '1', metadata: {...} },
 * { title: 'Page 2', page_no: '2', metadata: {...} }
 * ];
 *
 * // Transform entries before sending to API
 * const apiPayload = transformFieldsInEntries(entries, {
 * title: (entry) => entry.title.toUpperCase(),
 * page_no: (entry) => `Page ${entry.page_no}`
 * });
 *
 * // apiPayload = [
 * //   { title: 'PAGE 1', page_no: 'Page 1', metadata: {...} },
 * //   { title: 'PAGE 2', page_no: 'Page 2', metadata: {...} }
 * // ]
 * ```
 */
export const transformFieldsInEntries = <TEntry extends CommonEntityMetadata<TEntry>>(
  entries: TEntry[],
  transformers: { [TKey in keyof TEntry]?: (entry: TEntry, field: TKey) => TEntry[TKey] },
) => {
  return entries.map((entry) => {
    const newEntry = { ...entry };
    forEach(transformers, (transformer, field) => {
      if (typeof transformer === 'function') {
        newEntry[field as keyof TEntry] = transformer(entry, field as keyof TEntry);
      }
    });
    return newEntry;
  });
};

/**
 * Retrieves the original value of a field from an entry's metadata tracking.
 *
 * This utility function is used to access the original value of a field that has been
 * modified. It checks if there's a recorded change in the metadata and returns either
 * the original value from the change tracking or the current field value if no changes
 * have been recorded.
 *
 * @param entry - The entity with CommonEntityMetadata to retrieve the original value from
 * @param field - The field name to get the original value for
 * @returns The original value of the field, or the current value if no original exists
 *
 * @example
 * ```typescript
 * const entry = {
 *   title: 'New Title',
 *   metadata: {
 *     fieldChanges: {
 *       title: { originalValue: 'Old Title', newValue: 'New Title' }
 *     }
 *   }
 * };
 *
 * const origTitle = originalValue(entry, 'title'); // Returns 'Old Title'
 *
 * const entry2 = {
 *   title: 'New Title',
 *   metadata: {
 *     fieldChanges: {}
 *   }
 * };
 *
 * const origTitle2 = originalValue(entry2, 'title'); // Returns 'New Title'
 * ```
 */
export const getOriginalValue = <TEntry extends CommonEntityMetadata<TEntry>>(entry: TEntry, field: keyof TEntry) =>
  entry.metadata.fieldChanges?.[field]?.originalValue ?? entry[field];

/**
 * Processes entries and categorizes them into create, delete, and update operations for deck management.
 *
 * This function handles the complex logic of determining how to persist entity changes:
 * - New entries (isNewRow=true) → create operations
 * - Deleted entries (isDeleted=true) → delete operations
 * - Modified entries → update operations, unless protected fields changed
 * - Modified entries with protected field changes → delete + create operations
 *
 * @param entries - Array of entries with CommonEntityMetadata to process
 * @param protectedFields - Fields that cannot be updated in-place (typically used for unique IDs).
 *   When these fields change, the entry must be deleted and recreated.
 * @param fieldGenerators - Optional callbacks to generate new values for specific fields
 *   when creating entries from protected field modifications. These are useful to set time-based fields (`created_at`)
 *   and other fields which require unique values which we do not directly control (`scenario_id`, `toc_id`, etc.)
 *   NOTE: These callbacks are NOT called for entries where isNewRow was originally `true`.
 *
 * @returns Object containing arrays of entries categorized by operation type:
 *   - `create`: New entries or entries recreated due to protected field changes
 *   - `delete`: Entries marked for deletion or original entries being replaced
 *   - `update`: Entries with safe modifications that can be updated in-place
 *
 * @example
 * ```typescript
 * const result = prepareEntriesForSaveDeckOperation(
 *   modifiedEntries, // all entries intended for SaveDeck
 *   ['scenario_id', 'deck_id'], // protected fields
 *   {
 *     // field generators used on the newEntry for protected-field changes.
 *     scenario_id: (newEntry) => newEntry.metadata.internalId,
 *     created_at: () => new Date().toISOString()
 *   }
 * );
 *
 * // result = {
 * //   create: [...],
 * //   delete: [...],
 * //   update: [...],
 * // }
 * ```
 */
export const prepareEntriesForSaveDeckOperation = <TEntry extends CommonEntityMetadata<TEntry>>(
  entries: TEntry[],
  protectedFields: Array<keyof TEntry>,
  fieldGenerators?: { [TField in keyof TEntry]?: (newCreatedEntry: TEntry, field: TField) => TEntry[TField] },
) => {
  const result = {
    create: [] as TEntry[],
    delete: [] as TEntry[],
    update: [] as TEntry[],
  };

  forEach(entries, (entry) => {
    // If marked as a new row, move the entry to the `create` list and stop processing.
    if (entry.metadata.isNewRow) {
      result.create.push(entry);
      return;
    }

    // If marked as a deleted row, move the entry to the `delete` list and stop processing.
    if (entry.metadata.isDeleted) {
      result.delete.push(entry);
      return;
    }

    if (entry.metadata.isModified) {
      // NOTE: We need metadata.fieldChanges to exist to determine which fields have changed.
      if (entry.metadata?.fieldChanges) {
        const hasProtectedFieldChanges = protectedFields.some((field) => field in entry.metadata.fieldChanges!);
        // if hasProtectedFieldChanges is true, we must construct 2 entries from this one: one for delete and another for create.
        // For the "delete" entry, we must restore the original values which are protected so tha they match to the service.
        // For the "create" entry, we should use the newValues (which are already assigned to their respective fields).
        if (hasProtectedFieldChanges) {
          // Restore original values for our `delete` entry
          const deleteEntry = resetEntry(entry);
          deleteEntry.metadata.isDeleted = true;
          deleteEntry.metadata.isModified = true;
          deleteEntry.metadata.isNewRow = false;
          // Add the entry to the "delete" list.
          result.delete.push(deleteEntry);

          // Update metadata for the createEntry (mainly for sanity's sake in debugging)
          const createEntry = cloneDeep(entry);
          createEntry.metadata.internalId = createId();
          createEntry.metadata.isDeleted = false;
          createEntry.metadata.isModified = true;
          createEntry.metadata.isNewRow = true;

          // If we have updateFieldsOnCreate callbacks, run them for this "create" entry.
          if (fieldGenerators) {
            const keys = Object.keys(fieldGenerators) as Array<keyof TEntry>;
            keys.forEach((key) => {
              if (typeof fieldGenerators[key] === 'function') {
                createEntry[key] = fieldGenerators[key](createEntry, key);
              }
            });
          }

          // Add the entry to the "create" list.
          result.create.push(createEntry);
        } else {
          // Otherwise, this entry is a safe modification where only an "update" object is needed.
          result.update.push(entry);
        }
      } else {
        // If `metadata.fieldChanges` doesn't exist, assume it is a safe update.
        result.update.push(entry);
      }
    }
  });

  return result;
};

/**
 * Handles updating a field on an entity with CommonEntityMetadata, tracking changes and maintaining modification state.
 *
 * This function provides immutable field updates with automatic change tracking for entities that extend
 * CommonEntityMetadata. It maintains a history of field changes, allowing the system to determine what
 * has been modified and whether changes can be reverted to original values.
 *
 * Key behaviors:
 * - Creates a deep clone of the entry to ensure immutability
 * - Tracks original and new values in metadata.fieldChanges for existing rows
 * - Automatically updates metadata.isModified based on whether any changes exist
 * - Removes field changes when values are reverted to original
 * - Skips change tracking for new rows (isNewRow=true)
 *
 * @param entry - The entity to update, must extend CommonEntityMetadata
 * @param field - The field name to update (cannot be 'metadata')
 * @param newValue - The new value to assign to the field
 * @returns A new entry object with the field updated and metadata synchronized
 * @throws Error when attempting to modify the 'metadata' field directly
 *
 * @example
 * ```typescript
 * // originalEntry = {
 * //   title: 'Old Title',
 * //   metadata: { internalId: 'abc123', isModified: false, fieldChanges: {} }
 * // }
 *
 * const updatedEntry = changeEntryField(originalEntry, 'title', 'New Title');
 * // updatedEntry.title === 'New Title'
 * // updatedEntry.metadata.isModified === true
 * // updatedEntry.metadata.fieldChanges.title === { originalValue: 'Old Title', newValue: 'New Title' }
 *
 * const revertedEntry = changeEntryField(updatedEntry, 'title', 'Old Title');
 * // revertedEntry.title === 'Old Title'
 * // revertedEntry.metadata.isModified === false
 * // revertedEntry.metadata.fieldChanges.title === undefined
 * ```
 */
export const changeEntryField = <TEntry extends CommonEntityMetadata<TEntry>, TField extends keyof TEntry>(
  entry: TEntry,
  field: TField,
  newValue: TEntry[TField],
): TEntry => {
  if (field === 'metadata') {
    throw new Error('[handleFieldChange] Changing the `metadata` field is not permitted!');
  }

  const updatedEntry = cloneDeep(entry);
  // Only handle fieldChange tracking for existing rows (not new ones)
  if (!updatedEntry.metadata.isNewRow) {
    // If entry.metadata.fieldChanges is undefined, create it as an empty object.
    if (!updatedEntry.metadata.fieldChanges) {
      updatedEntry.metadata.fieldChanges = {} as MetadataFieldChangeMap<TEntry>;
    }

    const existingChange = updatedEntry.metadata.fieldChanges[field];
    const originalValue = (existingChange ? existingChange.originalValue : entry[field]) as TEntry[TField];

    const isNullish = (val: unknown) => val === null || val === undefined || val === '';
    const valuesAreEqual = isEqual(originalValue, newValue) || (isNullish(originalValue) && isNullish(newValue));

    if (valuesAreEqual) {
      // If the newValue matches the original, clear it from the fieldChanges
      delete updatedEntry.metadata.fieldChanges[field];
    } else {
      // No match to original, so let's record the change in this field.
      updatedEntry.metadata.fieldChanges = {
        ...updatedEntry.metadata.fieldChanges,
        [field]: { originalValue, newValue },
      };
    }
    // Update `isModified` based on if fieldChanges entries are present.
    updatedEntry.metadata.isModified = !isEmpty(updatedEntry.metadata.fieldChanges);
  }

  return {
    ...updatedEntry,
    [field]: newValue,
  };
};

/**
 * Handles updating multiple fields on an entity with CommonEntityMetadata.
 *
 * This function applies multiple field updates in a single operation, returning
 * a new entry with all specified fields updated and change tracking maintained.
 * It leverages {@link changeEntryField} internally to ensure consistent behavior across
 * all field updates.
 *
 * @param entry - The entity to update, must extend CommonEntityMetadata
 * @param changes - An object mapping field names to their new values
 * @returns A new entry object with all specified fields updated and metadata synchronized
 *
 * @example
 * ```typescript
 * const updatedEntry = changeMultipleEntryFields(originalEntry, {
 *   title: 'New Title',
 *   description: 'New Description'
 * });
 * ```
 */
export const changeMultipleEntryFields = <TEntry extends CommonEntityMetadata<TEntry>>(
  entry: TEntry,
  changes: { [TField in keyof TEntry]?: TEntry[TField] },
): TEntry => {
  return Object.entries(changes).reduce(
    (updatedEntry, [field, newValue]) => changeEntryField(updatedEntry, field as keyof TEntry, newValue as never),
    entry,
  );
};

/**
 * Retrieves the list of modified fields from an entity's metadata.
 *
 * This utility function extracts the field names that have been modified
 * based on the `fieldChanges` tracking in the entity's metadata.
 *
 * NOTE: This function is recommended over just calling `Object.keys(...)` directly
 * in case future `metadata` structure changes occur in the future.
 *
 * @param entry - The entity with CommonEntityMetadata to inspect
 * @returns An array of field names that have been modified
 */
export const getModifiedFields = <TEntry extends CommonEntityMetadata<TEntry>>(entry: TEntry) => {
  if (!entry.metadata.isModified) return [];

  return Object.keys(entry.metadata.fieldChanges || {}) as Array<keyof TEntry>;
};
